/**
 * What the selected moment actually was: the photo, the verdict and its
 * reasoning, what the worker said, or the alert that fired. One render path
 * per event type, all of them on the shared surface, badge and quote styles.
 */
import type { ReplayEvent } from '../../api/replay';
import { cn } from '@/lib/utils';
import { PhotoThumb, StatusBadge } from '../patterns';
import { EVENT_STYLES, toneClass } from './eventStyles';
import { timeOfDay } from './time';

interface Props {
  selectedEvent: ReplayEvent | null;
  /** Open a photo in the page lightbox. */
  onPhoto?: (src: string) => void;
}

/** A quote: the machine's words, or the worker's, set off from our own. */
function Quote({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="mt-3 border-l-2 border-ev/50 bg-sunken py-2 pr-3 pl-3 text-support text-ink">
      {children}
    </blockquote>
  );
}

function Aside({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <div className="text-label tracking-[0.08em] uppercase text-ink-faint">{label}</div>
      <div className="mt-1 text-support text-ink-dim">{children}</div>
    </div>
  );
}

export function ReplayDetailsPanel({ selectedEvent, onPhoto }: Props) {
  if (!selectedEvent) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
        <div className="text-subtitle text-ink">No moment selected</div>
        <p className="max-w-[40ch] text-support text-ink-dim">
          Pick an event on the timeline or in the list to see it here.
        </p>
      </div>
    );
  }

  const style = EVENT_STYLES[selectedEvent.type];

  return (
    <div
      data-testid={`replay-details-${selectedEvent.type}`}
      className={cn('p-4 sm:p-5', toneClass(selectedEvent.type))}
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-3">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-ev/40 bg-ev/10 px-2.5 py-0.5 text-meta font-semibold text-ink">
          <span className="size-2 rounded-full bg-ev" aria-hidden />
          {style.label}
        </span>
        <span className="text-meta text-ink-faint tabular-nums">
          {timeOfDay(selectedEvent.timestamp)}
          {selectedEvent.stepNumber !== null ? ` · Step ${selectedEvent.stepNumber}` : ''}
        </span>
      </header>

      <div className="pt-3">{renderBody(selectedEvent, onPhoto)}</div>
    </div>
  );
}

function renderBody(event: ReplayEvent, onPhoto?: (src: string) => void) {
  switch (event.type) {
    case 'session.started':
      return <p className="text-support text-ink-dim">The technician began the procedure.</p>;

    case 'step.started':
      return <p className="text-support text-ink-dim">Step {event.stepNumber} started.</p>;

    case 'step.photo_captured':
      return (
        <figure className="m-0 flex items-start gap-4">
          <PhotoThumb
            src={event.payload.photoUrl}
            alt={`Step ${event.stepNumber ?? '?'} verification photo`}
            onOpen={onPhoto}
            className="h-36 w-28"
          />
          <figcaption className="min-w-0 text-meta text-ink-dim">
            <div className="text-support text-ink">Verification photo</div>
            <div className="mt-1">Captured at {timeOfDay(event.payload.capturedAt)}</div>
            {onPhoto ? <div className="mt-1 text-ink-faint">Click the photo to enlarge.</div> : null}
          </figcaption>
        </figure>
      );

    case 'step.verified': {
      const { verdict, verdictText, confidence } = event.payload;
      const tone = verdict === 'pass' ? 'verified' : verdict === 'fail' ? 'error' : 'retry';
      return (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={tone}>{verdict}</StatusBadge>
            <span className="text-meta text-ink-faint">
              {Math.round(confidence * 100)}% confidence
            </span>
          </div>
          {verdictText ? <Quote>{verdictText}</Quote> : null}
        </>
      );
    }

    case 'step.retry':
      return (
        <>
          <p className="text-support font-medium text-ink">Claude asked for the photo again.</p>
          <Quote>{event.payload.reason || 'No reason given.'}</Quote>
          {event.payload.previousVerdictText ? (
            <Aside label="Previous verdict">{event.payload.previousVerdictText}</Aside>
          ) : null}
        </>
      );

    case 'worker_dialogue': {
      const { intent, severity, transcript, aiResponse } = event.payload;
      return (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge>{intent.replace(/_/g, ' ')}</StatusBadge>
            {severity ? <StatusBadge tone="error">{severity}</StatusBadge> : null}
          </div>
          <Quote>{transcript}</Quote>
          {aiResponse ? <Aside label="Answer">{aiResponse}</Aside> : null}
        </>
      );
    }

    case 'safety_alert':
      return (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone="error">{event.payload.severity}</StatusBadge>
            <span className="text-meta text-ink-faint">
              {event.payload.detectedBy === 'ai' ? 'AI monitor' : 'Critical-keyword fast path'}
            </span>
          </div>
          <p className="mt-3 text-prose font-medium text-ink">{event.payload.summary}</p>
          <Aside label="Recommended action">{event.payload.recommendedAction}</Aside>
        </>
      );

    case 'session.ended':
      return <p className="text-support text-ink-dim">The session closed.</p>;
  }
}
